import { ref } from 'vue'
import { defineStore } from 'pinia'
import { UserCreditsService } from '../services/user-credits.service'
import { downloadBlob } from '@/utils/file'
import type {
  CreditsSummary,
  UserCreditDetail,
  UserCreditSlider,
  ApiCreditOverview,
  ApiUsageHistoryItem,
  CurrencyPricing,
  PurchaseCreditCommand,
  CheckoutSessionResult,
} from '../types/user-credits.types'

const POLL_INTERVAL_MS = 2000
const MAX_POLL_DURATION_MS = 30 * 60 * 1000
const MAX_CONSECUTIVE_FAILURES = 3

export const useUserCreditsStore = defineStore('userCredits', () => {
  const service = new UserCreditsService()

  // State
  const summary = ref<CreditsSummary | null>(null)
  const userDetails = ref<UserCreditDetail[]>([])
  const creditSliders = ref<Record<string, UserCreditSlider>>({})
  const apiOverview = ref<ApiCreditOverview | null>(null)
  const apiUsageHistory = ref<ApiUsageHistoryItem[]>([])
  const apiUsageCreditsOverview = ref<{ totalLeadCredits: number; totalContactCredits: number }>({
    totalLeadCredits: 0,
    totalContactCredits: 0,
  })
  const pricePackages = ref<Record<string, CurrencyPricing> | null>(null)
  // Drives the global Add-Credits purchase dialog (opened from the top-nav button, so it must
  // live in the store rather than a single page).
  const purchaseDialogOpen = ref(false)
  const loading = ref(false)
  // Separate flag for details-only reloads (pagination / sort). The summary cards
  // above the table must NOT react to this — they don't change when paging rows.
  const detailsLoading = ref(false)
  const saving = ref(false)
  const error = ref<string | null>(null)
  const isPolling = ref(false)

  // ─── TAMI Credits ───

  function mapSummary(summaryData: Awaited<ReturnType<typeof service.getSummary>>): CreditsSummary {
    return {
      planName: summaryData.PlanName,
      planEnd: summaryData.PlanEnd,
      totalExportCredits: summaryData.TotalExportCredits,
      totalMobileCredits: summaryData.TotalMobileCredits,
      assignedExportCredits: summaryData.AssignedExportCredits,
      assignedMobileCredits: summaryData.AssignedMobileCredits,
      totalExportPurchases: summaryData.TotalExportPurchases,
      totalMobilePurchases: summaryData.TotalMobilePurchases,
      unassignedExportCredits: summaryData.UnassignedExportCredits,
      unassignedMobileCredits: summaryData.UnassignedMobileCredits,
      estimatedOutOfCreditsDate: summaryData.EstimatedOutOfCreditsDate,
      usersAmount: summaryData.UsersAmount,
      totalUsers: summaryData.TotalUsers,
      activeUserAmount: summaryData.ActiveUserAmount,
      inactiveUserAmount: summaryData.InactiveUserAmount,
      isFullContract: summaryData.IsFullContract,
    }
  }

  async function loadSummaryAndDetails(
    page: number,
    pageSize: number,
    sortBy: string,
    sortDesc: boolean,
  ): Promise<void> {
    loading.value = true
    error.value = null
    // Load summary and details INDEPENDENTLY. The Summary endpoint can exceed the
    // 30s client timeout for large accounts; when it's canceled we must still render
    // the details table (which loads fine) instead of blanking the whole page — the
    // old Promise.all discarded a successful Details response whenever Summary failed.
    try {
      const [summaryResult, detailsResult] = await Promise.allSettled([
        service.getSummary(),
        service.getDetails(page, pageSize, sortBy, sortDesc),
      ])

      if (summaryResult.status === 'fulfilled') {
        summary.value = mapSummary(summaryResult.value)
      }

      if (detailsResult.status === 'fulfilled') {
        userDetails.value = mapUserDetails(detailsResult.value, summary.value?.isFullContract ?? false)
        initializeSliders()
      }

      if (summaryResult.status === 'rejected' && detailsResult.status === 'rejected') {
        error.value = 'Failed to load user credits data'
        userDetails.value = []
        creditSliders.value = {}
      } else if (summaryResult.status === 'rejected') {
        error.value = 'The credits summary took too long to load. The allocations below are still available.'
      } else if (detailsResult.status === 'rejected') {
        error.value = 'Failed to load user credit allocations.'
      }
    } finally {
      loading.value = false
    }
  }

  // Maps the raw details payload into UserCreditDetail rows. Shared by the initial
  // full load and details-only reloads (pagination / sort).
  function mapUserDetails(
    detailsData: Awaited<ReturnType<typeof service.getDetails>>,
    isFullContract: boolean,
  ): UserCreditDetail[] {
    return detailsData.map((u) => {
      const assignedExport = u.CurrentExportCredits ?? 0
      const assignedMobile = u.CurrentMobileCredits ?? 0
      const exportPurchasedMonth = u.PurchasedExportForMonth
      const exportPurchasedTotal = u.TotalExportPurchased
      const mobilePurchasedMonth = u.PurchasedMobileForMonth
      const mobilePurchasedTotal = u.TotalMobilePurchased

      let exportBalance: number
      let mobileBalance: number
      if (!u.Active) {
        exportBalance = 0
        mobileBalance = 0
      } else if (isFullContract) {
        exportBalance = assignedExport - exportPurchasedTotal
        mobileBalance = assignedMobile - mobilePurchasedTotal
      } else {
        exportBalance = assignedExport - exportPurchasedMonth
        mobileBalance = assignedMobile - mobilePurchasedMonth
      }

      return {
        id: u.Id,
        user: `${u.ContactName} ${u.Lastname}`,
        userType: u.Role,
        email: u.Email,
        active: u.Active,
        status: u.Active ? 'Active' : 'Inactive',
        assignedExportCredits: assignedExport,
        assignedMobileCredits: assignedMobile,
        exportBalance,
        mobileBalance,
        exportPurchasedMonth,
        exportPurchasedTotal,
        mobilesPurchasedMonth: mobilePurchasedMonth,
        mobilesPurchasedTotal: mobilePurchasedTotal,
        searchesMonth: u.SearchesForMonth,
        searchesTotal: u.TotalSearched,
        lastLogin: u.LastLogin,
      } as UserCreditDetail
    })
  }

  // Reloads only the table rows (pagination / sort changes). Leaves the summary and
  // its cards untouched, and toggles detailsLoading — not the shared loading flag.
  async function loadDetails(
    page: number,
    pageSize: number,
    sortBy: string,
    sortDesc: boolean,
  ): Promise<void> {
    detailsLoading.value = true
    error.value = null
    try {
      const detailsData = await service.getDetails(page, pageSize, sortBy, sortDesc)
      userDetails.value = mapUserDetails(detailsData, summary.value?.isFullContract ?? false)
      initializeSliders()
    } catch {
      error.value = 'Failed to load user credits data'
      userDetails.value = []
      creditSliders.value = {}
    } finally {
      detailsLoading.value = false
    }
  }

  function initializeSliders(): void {
    if (!summary.value) return

    const sliders: Record<string, UserCreditSlider> = {}
    const unassignedExport = summary.value.unassignedExportCredits
    const unassignedMobile = summary.value.unassignedMobileCredits
    const isFullContract = summary.value.isFullContract

    for (const user of userDetails.value) {
      let minExportValue: number
      let minMobileValue: number
      if (!user.active) {
        minExportValue = 0
        minMobileValue = 0
      } else if (isFullContract) {
        minExportValue = user.exportPurchasedTotal
        minMobileValue = user.mobilesPurchasedTotal
      } else {
        minExportValue = user.exportPurchasedMonth
        minMobileValue = user.mobilesPurchasedMonth
      }

      // Clamp slider value to at least the min value.
      // Vue 2 parity: when CurrentCredits is null/0 but purchases exist,
      // the slider clamps to min (purchased amount) rather than showing 0.
      const exportValue = Math.max(user.assignedExportCredits, minExportValue)
      const mobileValue = Math.max(user.assignedMobileCredits, minMobileValue)

      const exportDisabled = !user.active || (user.exportBalance === 0 && unassignedExport === 0)
      const mobileDisabled = !user.active || (user.mobileBalance === 0 && unassignedMobile === 0)

      const exportAdjustment = exportDisabled ? 1 : 0
      const mobileAdjustment = mobileDisabled ? 1 : 0

      sliders[user.id] = {
        exportValue,
        mobileValue,
        availableExportCredits: exportValue + unassignedExport + exportAdjustment,
        availableMobileCredits: mobileValue + unassignedMobile + mobileAdjustment,
        minExportValue,
        minMobileValue,
        exportDisabled,
        mobileDisabled,
      }
    }

    creditSliders.value = sliders
  }

  function updateSlider(userId: string, field: 'export' | 'mobile', value: number): void {
    if (!summary.value) return

    const slider = creditSliders.value[userId]
    if (!slider) return

    const isExport = field === 'export'
    const oldValue = isExport ? slider.exportValue : slider.mobileValue
    const delta = value - oldValue

    if (isExport) {
      slider.exportValue = value
      summary.value.assignedExportCredits += delta
      summary.value.unassignedExportCredits -= delta
    } else {
      slider.mobileValue = value
      summary.value.assignedMobileCredits += delta
      summary.value.unassignedMobileCredits -= delta
    }

    const isFullContract = summary.value.isFullContract

    for (const user of userDetails.value) {
      const s = creditSliders.value[user.id]
      if (!s) continue

      // Recalculate balance using slider value
      if (!user.active) {
        user.exportBalance = 0
        user.mobileBalance = 0
      } else if (isFullContract) {
        user.exportBalance = s.exportValue - user.exportPurchasedTotal
        user.mobileBalance = s.mobileValue - user.mobilesPurchasedTotal
      } else {
        user.exportBalance = s.exportValue - user.exportPurchasedMonth
        user.mobileBalance = s.mobileValue - user.mobilesPurchasedMonth
      }

      // Recalculate disable (uses company-level pool)
      s.exportDisabled = !user.active || (user.exportBalance === 0 && summary.value.unassignedExportCredits === 0)
      s.mobileDisabled = !user.active || (user.mobileBalance === 0 && summary.value.unassignedMobileCredits === 0)

      // Recalculate available with adjustment
      const exportAdj = s.exportDisabled ? 1 : 0
      const mobileAdj = s.mobileDisabled ? 1 : 0
      s.availableExportCredits = s.exportValue + summary.value.unassignedExportCredits + exportAdj
      s.availableMobileCredits = s.mobileValue + summary.value.unassignedMobileCredits + mobileAdj
    }
  }

  async function saveUserCredits(userId: string): Promise<boolean> {
    const slider = creditSliders.value[userId]
    if (!slider) return false

    saving.value = true
    try {
      const result = await service.saveCredits({
        Users: [
          {
            Id: userId,
            ExportCredits: slider.exportValue,
            MobileCredits: slider.mobileValue,
          },
        ],
      })

      if (result.result) {
        const user = userDetails.value.find((u) => u.id === userId)
        if (user) {
          user.assignedExportCredits = slider.exportValue
          user.assignedMobileCredits = slider.mobileValue
        }
      }

      return result.result
    } catch {
      error.value = 'Failed to save credit allocation'
      return false
    } finally {
      saving.value = false
    }
  }

  // ─── API Credits ───

  async function loadApiOverview(): Promise<void> {
    try {
      apiOverview.value = await service.getApiCreditOverview()
    } catch {
      error.value = 'Failed to load API credit overview'
    }
  }

  async function loadApiUsageHistory(startDate: string, endDate: string): Promise<void> {
    loading.value = true
    try {
      const data = await service.getApiUsageHistory(startDate, endDate)
      apiUsageHistory.value = data.apiUsage ?? []
      apiUsageCreditsOverview.value = {
        totalLeadCredits: data.totalCompanyCreditsUsed ?? 0,
        totalContactCredits: data.totalContactCreditsUsed ?? 0,
      }
    } catch {
      error.value = 'Failed to load API usage history'
      apiUsageHistory.value = []
    } finally {
      loading.value = false
    }
  }

  async function downloadApiReport(startDate: string, endDate: string): Promise<void> {
    try {
      const blob = await service.downloadApiReport(startDate, endDate)
      downloadBlob(blob, 'ApiUsageHistory.xlsx')
    } catch {
      error.value = 'Failed to download API report'
    }
  }

  // ─── Shared ───

  async function requestCredits(): Promise<void> {
    try {
      await service.requestAdditionalCredits()
    } catch {
      // Fire-and-forget — matching Vue 2 behavior
    }
  }

  // ─── Purchase Credits ───

  async function loadPricePackages(): Promise<void> {
    try {
      const data = await service.getPricePackages()
      const normalized: Record<string, CurrencyPricing> = {}
      for (const [currency, pricing] of Object.entries(data)) {
        normalized[currency] = {
          export: (pricing as any).Export?.map((p: any) => ({ amount: p.Amount, price: p.Price })) ?? [],
          mobile: (pricing as any).Mobile?.map((p: any) => ({ amount: p.Amount, price: p.Price })) ?? [],
        }
      }
      pricePackages.value = normalized
    } catch {
      error.value = 'Failed to load price packages'
    }
  }

  // Lazily loads the price packages (if not already) and opens the global purchase dialog.
  async function openPurchaseDialog(): Promise<void> {
    if (!pricePackages.value) await loadPricePackages()
    purchaseDialogOpen.value = true
  }

  async function purchaseCredits(command: PurchaseCreditCommand): Promise<CheckoutSessionResult | null> {
    try {
      const result = await service.purchaseCredit(command)
      if (!result.result) {
        error.value = 'Purchase failed'
        return null
      }
      return result
    } catch {
      error.value = 'Failed to initiate purchase'
      return null
    }
  }

  // Identifies the active poll loop. Bumped when a new poll starts or polling is cancelled, so a
  // stale loop — one left running after the user clicked Cancel, or a second "Proceed to payment" —
  // sees the mismatch and stops instead of firing GetCheckoutSession forever. A single shared
  // isPolling boolean could not tell two overlapping loops apart.
  let pollGeneration = 0

  async function pollCheckoutSession(sessionId: string): Promise<'complete' | 'expired' | 'cancelled' | 'error'> {
    const generation = ++pollGeneration
    isPolling.value = true
    const startTime = Date.now()
    let consecutiveFailures = 0

    // Clear the shared flag only if we're still the current loop — a newer poll may own it now.
    const finish = () => {
      if (generation === pollGeneration) isPolling.value = false
    }

    return new Promise((resolve) => {
      const poll = async () => {
        if (generation !== pollGeneration) {
          resolve('cancelled')
          return
        }

        if (Date.now() - startTime > MAX_POLL_DURATION_MS) {
          finish()
          resolve('error')
          return
        }

        try {
          const session = await service.getCheckoutSession(sessionId)
          consecutiveFailures = 0

          // Honor a completed payment even if a cancel raced in while this request was in flight
          // (e.g. the user closed the Stripe tab a moment after paying).
          if (session.status === 'complete') {
            finish()
            setTimeout(() => {
              loadSummaryAndDetails(1, 15, 'name', false)
            }, 60000)
            resolve('complete')
            return
          }

          if (generation !== pollGeneration) {
            resolve('cancelled')
            return
          }

          if (session.status === 'expired') {
            finish()
            resolve('expired')
            return
          }

          setTimeout(poll, POLL_INTERVAL_MS)
        } catch {
          if (generation !== pollGeneration) {
            resolve('cancelled')
            return
          }
          consecutiveFailures++
          if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
            finish()
            resolve('error')
            return
          }
          setTimeout(poll, POLL_INTERVAL_MS)
        }
      }

      setTimeout(poll, POLL_INTERVAL_MS)
    })
  }

  function cancelPolling(): void {
    // Invalidate the active loop (it stops on its next tick / after its in-flight request) and clear
    // the public flag.
    pollGeneration++
    isPolling.value = false
  }

  function $reset(): void {
    summary.value = null
    userDetails.value = []
    creditSliders.value = {}
    apiOverview.value = null
    apiUsageHistory.value = []
    apiUsageCreditsOverview.value = { totalLeadCredits: 0, totalContactCredits: 0 }
    pricePackages.value = null
    purchaseDialogOpen.value = false
    loading.value = false
    saving.value = false
    error.value = null
    isPolling.value = false
  }

  return {
    summary,
    userDetails,
    creditSliders,
    apiOverview,
    apiUsageHistory,
    apiUsageCreditsOverview,
    pricePackages,
    purchaseDialogOpen,
    loading,
    detailsLoading,
    saving,
    error,
    isPolling,
    loadSummaryAndDetails,
    loadDetails,
    updateSlider,
    saveUserCredits,
    loadApiOverview,
    loadApiUsageHistory,
    downloadApiReport,
    requestCredits,
    loadPricePackages,
    openPurchaseDialog,
    purchaseCredits,
    pollCheckoutSession,
    cancelPolling,
    $reset,
  }
})
